import { Search } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import "@/styles/search.css";

import { DesktopSearchPalette } from "./DesktopSearchPalette";
import { MobileSearchOverlay } from "./MobileSearchOverlay";
import { primeMobileKeyboard } from "./primeMobileKeyboard";
import { useSearchController } from "./useSearchController";

// Whether the keyboard shortcut fired while the user is typing in a field, in
// which case it must not open the dialog. contenteditable covers rich text
// editors (Tiptap, Lexical...), which have no such field today but commonly
// bind Ctrl/Cmd+K to "insert link".
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
};

const SHORTCUT_LABEL =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.userAgent)
    ? "⌘K"
    : "Ctrl K";

/**
 * Opens the global search dialog. `variant="pill"` is the wide glass search
 * field of the mobile top row, `variant="icon"` a compact icon-only trigger,
 * `variant="desk"` the bordered pill of the desktop sheet header;
 * the default renders an input-like trigger for the desktop app bar.
 */
export const GlobalSearchButton = ({
  variant = "labelled",
}: {
  variant?: "labelled" | "icon" | "pill" | "desk";
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const originRef = useRef<HTMLButtonElement>(null);
  const { key: locationKey } = useLocation();

  // The search pushes no history entry of its own, so any route change
  // (the browser Back button, a link, the hash edited by hand) means the
  // user has left it: close it instead of floating over the new page.
  useEffect(() => {
    setOpen(false);
  }, [locationKey]);

  const openSearch = () => {
    if (isMobile) primeMobileKeyboard();
    setOpen(true);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "k" || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }
      event.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      {variant === "desk" ? (
        // Bordered search pill of the desktop sheet header (desk-shell.css).
        <button
          ref={originRef}
          type="button"
          onClick={openSearch}
          aria-label={translate("crm.search.title")}
          aria-keyshortcuts="Control+K Meta+K"
          className="desk-search desk-press"
        >
          <Search aria-hidden className="size-4 shrink-0" strokeWidth={1.5} />
          <span className="desk-search__placeholder">
            {translate("desk.shell.search_placeholder")}
          </span>
          <kbd className="desk-search__kbd">{SHORTCUT_LABEL}</kbd>
        </button>
      ) : variant === "icon" ? (
        <Button
          ref={originRef}
          variant="ghost"
          size="icon"
          // same shape and icon size as its neighbour, MobileRefreshButton
          className="rounded-full"
          onClick={openSearch}
          aria-label={translate("crm.search.title")}
        >
          <Search className="size-5" />
        </Button>
      ) : variant === "pill" ? (
        <button
          ref={originRef}
          type="button"
          onClick={openSearch}
          aria-label={translate("crm.search.title")}
          // hidden while the overlay's input capsule stands in for it
          data-search-origin={open ? "covered" : undefined}
          className="liquid-glass glass-press pointer-events-auto flex h-[var(--mobile-control)] min-w-0 flex-1 items-center gap-2 rounded-full px-3.5 text-[var(--glass-muted)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <Search
            className="size-4 shrink-0 text-[var(--glass-foreground)]"
            strokeWidth={2.25}
          />
          <span className="truncate text-[15px]">
            {translate("crm.search.title")}
          </span>
        </button>
      ) : (
        // Looks like a search field, so its purpose reads at a glance; the
        // real input lives in the dialog it opens.
        <Button
          ref={originRef}
          variant="outline"
          onClick={openSearch}
          aria-label={translate("crm.search.title")}
          aria-keyshortcuts="Control+K Meta+K"
          className="mr-2 w-40 justify-start bg-background px-3 font-normal text-muted-foreground lg:w-64"
        >
          <Search />
          <span className="flex-1 text-left">
            {translate("crm.search.title")}
          </span>
          <kbd className="pointer-events-none hidden rounded border bg-muted px-1.5 font-sans text-[10px] font-medium lg:inline">
            {SHORTCUT_LABEL}
          </kbd>
        </Button>
      )}
      <GlobalSearchDialog
        open={open}
        onOpenChange={setOpen}
        originRef={originRef}
      />
    </>
  );
};

/**
 * The global search: a full-height glass overlay on a phone, a Cmd/Ctrl+K
 * command palette on desktop. Both run the same server-side search
 * (useSearchController). `originRef` is the trigger the phone overlay's input
 * grows out of and focus returns to.
 */
export const GlobalSearchDialog = ({
  open,
  onOpenChange,
  originRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originRef?: RefObject<HTMLElement | null>;
}) => {
  const isMobile = useIsMobile();
  const search = useSearchController({ open, isMobile, onOpenChange });

  return isMobile ? (
    <MobileSearchOverlay open={open} search={search} originRef={originRef} />
  ) : (
    <DesktopSearchPalette open={open} search={search} />
  );
};
